import { defaultsDeep, get, pick, cloneDeep } from "lodash";

const defaultFieldsCache = new WeakMap();

function getDefaultFields(fields) {
  if (!defaultFieldsCache.has(fields)) {
    defaultFieldsCache.set(fields, cloneDeep(fields));
  }
  return defaultFieldsCache.get(fields);
}

export function sanitize(data = {}, fields) {
  return defaultsDeep(
    pick(cloneDeep(data), Object.keys(fields)),
    getDefaultFields(fields)
  );
}

export default class DataStructure {
  constructor(data) {
    // Sanitization is the child's resonsibility.
    //
    // Unwrap a structure passed where its payload was meant: the field
    // getters below are non-enumerable, so `Object.keys(anInstance)` is
    // exactly `["data"]` — wrapping one would build a `data` getter that
    // returns `this.data.data`, i.e. itself. Every later read then recursed
    // until the stack blew ("RangeError: Maximum call stack size exceeded"
    // in `Address.get [as data]`), taking the whole render down. Double
    // wrapping is a caller bug, but it must not be a fatal one, and
    // `new Address(anAddress)` should mean what it says.
    this.data = (data instanceof DataStructure ? data.data : data) || {};

    // Add a top-level getter for each field of data...
    Object.keys(this.data).forEach((key) => {
      // ...never for `data` itself (see above), and...
      if (key === "data") {
        return;
      }
      // ...only if it's not already defined
      if (!this.__proto__.hasOwnProperty(key)) {
        Object.defineProperty(this, key, {
          get: () => this.data[key],
        });
      }
    });
  }

  get validationErrors() {
    if (!this.rules) return [];
    const errors = [];
    for (const [key, rules] of Object.entries(this.rules)) {
      if (!Array.isArray(rules)) continue;
      for (const rule of rules) {
        const result = rule(get(this.data, key));
        if (result !== true && typeof result === "string") {
          errors.push(result);
        }
      }
    }
    return errors;
  }

  can(user, action) {
    if (!this.permissions) {
      return;
    }
    if (!user) {
      throw "Missing user";
    }

    if (action) {
      if (action in this.permissions) {
        return this.permissions[action].call(this, user);
      } else {
        throw `Invalid action: ${action}`;
      }
    } else {
      const result = {};
      Object.keys(this.permissions).forEach((action) => {
        result[action] = this.permissions[action].call(this, user);
      });
      return result;
    }
  }
}
